// Real API layer for the outreach feature.
// Every function name and return shape matches the mock contract so components need no changes.
import apiInstance from './apiAuth';

const BASE = 'outreach/';

// ---------- Campaigns ----------
export const listCampaigns = () =>
  apiInstance.get(`${BASE}campaigns/`);

export const getCampaign = (id) =>
  apiInstance.get(`${BASE}campaigns/${id}/`);

export const createCampaign = (payload) =>
  apiInstance.post(`${BASE}campaigns/`, payload);

export const updateCampaign = (id, patch) =>
  apiInstance.patch(`${BASE}campaigns/${id}/`, patch);

export const activateCampaign = (id) =>
  apiInstance.post(`${BASE}campaigns/${id}/activate/`);

export const pauseCampaign = (id) =>
  apiInstance.post(`${BASE}campaigns/${id}/pause/`);

export const resumeCampaign = (id) =>
  apiInstance.post(`${BASE}campaigns/${id}/resume/`);

export const archiveCampaign = (id) =>
  apiInstance.post(`${BASE}campaigns/${id}/archive/`);

export const stopCampaignEnrichment = (id) =>
  apiInstance.post(`${BASE}campaigns/${id}/stop-enrichment/`);

// ---------- Eligibility preview ----------
export const previewEligibility = (rules) =>
  apiInstance.post(`${BASE}segments/preview-count/`, { rules });

// ---------- Templates / Segments / Message templates ----------
export const listTemplates = () =>
  apiInstance.get(`${BASE}templates/`);

export const listMessageTemplates = (channel) =>
  apiInstance.get(`${BASE}message-templates/${channel ? `?channel=${channel}` : ''}`);

export const createMessageTemplate = (data) =>
  apiInstance.post(`${BASE}message-templates/`, data);

export const updateMessageTemplate = (id, data) =>
  apiInstance.patch(`${BASE}message-templates/${id}/`, data);

export const deleteMessageTemplate = (id) =>
  apiInstance.delete(`${BASE}message-templates/${id}/`);

export const listSegments = () =>
  apiInstance.get(`${BASE}segments/`);

export const createSegment = (payload) =>
  apiInstance.post(`${BASE}segments/`, payload);

export const deleteSegment = (id) =>
  apiInstance.delete(`${BASE}segments/${id}/`);

// ---------- Connected accounts ----------
// Normalize flat backend shape to the nested shape the components expect.
const normalizeOutlookAccount = (a) => ({
  ...a,
  user: { id: a.user_id, name: a.user_name },
  // Derive health from token expiry
  health: a.token_expires_at && new Date(a.token_expires_at) < new Date()
    ? 'token_expired'
    : 'healthy',
  sent_today: 0,          // not tracked per-day yet
  used_in_campaigns: 0,   // populated in future phase
});

const normalizeUnipileAccount = (a) => ({
  ...a,
  display_name: a.user_name || a.linkedin_handle,
  profile_name: a.linkedin_handle,
  owner_type: 'shared',
  notes: '',
  used_in_campaigns: 0,
});

// Both list endpoints accept an optional countryIds array — when present,
// the backend returns only accounts whose user is tagged with at least one
// of those Location ids. Pass `undefined` / empty to skip the filter.
const _countryParam = (countryIds) => {
  const ids = (countryIds || []).filter((n) => Number.isInteger(n) && n > 0);
  return ids.length ? `?country_ids=${ids.join(',')}` : '';
};

export const listOutlookAccounts = (countryIds) =>
  apiInstance.get(`${BASE}outlook-accounts/${_countryParam(countryIds)}`).then((res) => ({
    ...res,
    data: (res.data || []).map(normalizeOutlookAccount),
  }));

export const listUnipileAccounts = (countryIds) =>
  apiInstance.get(`${BASE}unipile-accounts/${_countryParam(countryIds)}`).then((res) => ({
    ...res,
    data: (res.data || []).map(normalizeUnipileAccount),
  }));

// Active users available for the reviewer multi-select on a campaign.
// Returns: [{ id, name, email, has_outlook, has_unipile }, ...]
export const listActiveUsers = () =>
  apiInstance.get(`${BASE}active-users/`).then((res) => ({
    ...res,
    data: res.data || [],
  }));

export const connectUnipileAccount = (payload) =>
  apiInstance.post(`${BASE}unipile-accounts/connect/`, payload);

// Hosted Auth Wizard — generate a one-shot Unipile-hosted login URL the user
// can open in a new tab. After they complete auth, Unipile's notify webhook
// will create the UnipileAccount row server-side.
export const createUnipileHostedLink = ({ providers = ['LINKEDIN'], type = 'create', user_id } = {}) =>
  apiInstance.post(`${BASE}unipile-accounts/hosted-link/`, { providers, type, user_id });

// Pull every account known to Unipile and create local rows for any not yet
// imported. Used when the hosted-auth notify webhook didn't reach us (dev tunnel
// down, etc.) or when accounts were added directly on the Unipile dashboard.
export const syncUnipileAccounts = () =>
  apiInstance.post(`${BASE}unipile-accounts/sync/`);

export const disconnectUnipileAccount = (id) =>
  apiInstance.delete(`${BASE}unipile-accounts/${id}/`);

export const checkUnipileAccountHealth = (id) =>
  apiInstance.post(`${BASE}unipile-accounts/${id}/check-health/`);

// ---------- My email signature ----------
// GET  returns: {profile: {full_name, designation, phone, mobile, teams_handle,
//                          linkedin_url, address_line1/2/3, is_configured},
//                 signature_html, has_account, email, pm_name}
// PATCH accepts the same profile keys (partial); returns the same shape.
export const getMySignature = () =>
  apiInstance.get(`${BASE}my-signature/`);

export const updateMySignature = (payload) =>
  apiInstance.patch(`${BASE}my-signature/`, payload);

// Live-preview: render proposed unsaved overrides without persisting.
export const previewMySignature = (payload) =>
  apiInstance.post(`${BASE}my-signature/preview/`, payload);

// Marks the underlying Company itself as invalid (moves it to 5% stage) —
// same endpoint the /companies/ dashboard hits. Distinct from
// `markCompanyInvalid` above, which acts on a campaign Enrollment.
export const markRawCompanyInvalid = (companyId, payload) =>
  apiInstance.post(`company/${companyId}/mark-invalid/`, payload);

// Admin funnel for a single campaign — counts across queue, enrollment,
// step executions, replies, plus per-PM breakdown + invalid-reason
// breakdown. Read-only.
export const getCampaignOverview = (campaignId, params = {}) =>
  apiInstance.get(`${BASE}campaigns/${campaignId}/overview/`, { params });

// Super-admin override: push an auto-rejected company into the review queue.
export const sendCompanyToReview = (campaignId, companyId) =>
  apiInstance.post(`${BASE}campaigns/${campaignId}/companies/${companyId}/send-to-review/`);

// ---------- Enrollments / Executions ----------
export const listEnrollments = ({ campaign_id, pm_id, status } = {}) => {
  if (campaign_id !== undefined) {
    const params = new URLSearchParams();
    if (pm_id !== undefined) params.append('pm_id', pm_id);
    if (status) params.append('status', status);
    const qs = params.toString();
    return apiInstance.get(`${BASE}campaigns/${campaign_id}/enrollments/${qs ? `?${qs}` : ''}`);
  }
  const params = new URLSearchParams();
  if (pm_id !== undefined) params.append('pm_id', pm_id);
  if (status) params.append('status', status);
  const qs = params.toString();
  return apiInstance.get(`${BASE}enrollments/${qs ? `?${qs}` : ''}`);
};

export const getEnrollment = (id) =>
  apiInstance.get(`${BASE}enrollments/${id}/`);

export const listExecutions = ({ enrollment_id } = {}) =>
  apiInstance.get(`${BASE}enrollments/${enrollment_id}/executions/`);

// Status keys understood by the filter chips → enrollment.status values on the backend.
const STATUS_KEY_TO_DB = {
  replied: 'STOPPED_REPLIED',
  active:  'ACTIVE',
  invalid: 'INVALID',
  done:    'COMPLETED',
};

export const getCampaignEnrollmentsForPM = (
  campaignId, pmId,
  { limit = 50, offset = 0, statusKey = 'all' } = {},
) => {
  const params = new URLSearchParams();
  if (pmId)   params.set('pm_id',  String(pmId));
  if (limit)  params.set('limit',  String(limit));
  if (offset) params.set('offset', String(offset));
  const dbStatus = STATUS_KEY_TO_DB[statusKey];
  if (dbStatus) params.set('status', dbStatus);
  return apiInstance.get(`${BASE}campaigns/${campaignId}/enrollments/?${params.toString()}`);
};

export const getCampaignEnrollmentStatusCounts = (campaignId, pmId) => {
  const qs = pmId ? `?pm_id=${pmId}` : '';
  return apiInstance.get(`${BASE}campaigns/${campaignId}/enrollment-status-counts/${qs}`);
};

// ---------- PM views ----------
// pmId is optional — backend defaults to the auth user; pass it when an admin views a PM's data.
export const getMyQueue = (pmId) => {
  const qs = pmId ? `?pm_id=${pmId}` : '';
  return apiInstance.get(`${BASE}my/queue/${qs}`);
};

export const getMyReplies = (pmId, { campaignId } = {}) => {
  // campaignId → all replies for that campaign (admin/reviewer campaign view).
  // pmId → replies owned by that PM. campaignId wins when both are passed.
  const params = new URLSearchParams();
  if (campaignId) params.set('campaign_id', String(campaignId));
  else if (pmId)  params.set('pm_id', String(pmId));
  const qs = params.toString();
  return apiInstance.get(`${BASE}my/replies/${qs ? `?${qs}` : ''}`);
};

// Paginated PM companies endpoint. Backwards-compat: callers that ignored the
// extra options arg still get a valid response (default page-1, no status).
export const getMyCompanies = (
  pmId,
  { limit = 50, offset = 0, statusKey = 'all', campaignId = null } = {},
) => {
  const params = new URLSearchParams();
  if (pmId)       params.set('pm_id', String(pmId));
  if (limit)      params.set('limit', String(limit));
  if (offset)     params.set('offset', String(offset));
  if (campaignId) params.set('campaign_id', String(campaignId));
  const STATUS_KEY_TO_DB = { replied: 'STOPPED_REPLIED', active: 'ACTIVE', invalid: 'INVALID', done: 'COMPLETED' };
  const dbStatus = STATUS_KEY_TO_DB[statusKey];
  if (dbStatus) params.set('status', dbStatus);
  return apiInstance.get(`${BASE}my/companies/?${params.toString()}`);
};

export const getMyCompaniesStatusCounts = (pmId) => {
  const qs = pmId ? `?pm_id=${pmId}` : '';
  return apiInstance.get(`${BASE}my/companies-status-counts/${qs}`);
};

export const markReplyRead = (replyId) =>
  apiInstance.patch(`${BASE}replies/${replyId}/read/`);

export const replyToThread = (replyId, body, { takeOver = false } = {}) =>
  apiInstance.post(`${BASE}replies/${replyId}/reply/`, { body, take_over: takeOver });

export const getLinkedInThread = (replyId) =>
  apiInstance.get(`${BASE}replies/${replyId}/thread/`);

export const takeOverThread = (replyId) =>
  apiInstance.post(`${BASE}replies/${replyId}/take-over/`);

export const markNotInterested = (replyId) =>
  // Stop the sequence for this reply's enrollment without sending a reply.
  apiInstance.post(`${BASE}replies/${replyId}/take-over/`);

export const pauseAllForPm = (pmId) =>
  apiInstance.post(`${BASE}my/pause-all/`, pmId ? { pm_id: pmId } : {});

// ---------- Company-level actions (from My Outreach) ----------
export const listInvalidReasons = () =>
  apiInstance.get('company/invalid-reasons/');

export const markCompanyInvalid = (enrollmentId, { reason_id, description } = {}) =>
  apiInstance.post(`${BASE}enrollments/${enrollmentId}/mark-invalid/`, { reason_id, description });

export const moveCompanyStage = (enrollmentId, stage, options = {}) =>
  apiInstance.post(`${BASE}enrollments/${enrollmentId}/move-stage/`, { stage, options });

export const skipExecution = (executionId) =>
  apiInstance.post(`${BASE}executions/${executionId}/skip/`);

export const sendNowExecution = (executionId) =>
  apiInstance.post(`${BASE}executions/${executionId}/send-now/`);

// Resume an enrollment that was auto-stopped by a reply.
// Caller (frontend) is responsible for the two-step PM confirmation.
export const resumeEnrollment = (enrollmentId) =>
  apiInstance.post(`${BASE}enrollments/${enrollmentId}/resume/`);

export const getMyCompanyDetail = (enrollmentId) =>
  apiInstance.get(`${BASE}enrollments/${enrollmentId}/detail/`);

// ---------- Live Monitor ----------
export const getLiveMonitor = () =>
  apiInstance.get(`${BASE}monitor/`);

// ---------- Natural-language rule parser ----------
export const parseRulesFromText = (text) =>
  apiInstance.post(`${BASE}rules/parse/`, { text });

// ---------- Filter options (real country/industry values from DB) ----------
export const getFilterOptions = () =>
  apiInstance.get(`${BASE}filter-options/`);

export const getOutreachConfig = () =>
  apiInstance.get(`${BASE}config/`);

// ---------- Team / PM performance (admin rollup) ----------
export const getTeamMetrics = () =>
  apiInstance.get(`${BASE}monitor/`);

export const getPmFunnel = (pmId) =>
  apiInstance.get(`${BASE}my/companies/?pm_id=${pmId}`);

// ---------- AI Discovery — enrichment review queue ----------
export const getCampaignQueueStats = (campaignId) =>
  apiInstance.get(`${BASE}campaigns/${campaignId}/queue/stats/`);

export const listCampaignQueue = (campaignId, params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return apiInstance.get(`${BASE}campaigns/${campaignId}/queue/${qs ? `?${qs}` : ''}`);
};

export const getQueueEntry = (entryId) =>
  apiInstance.get(`${BASE}queue/${entryId}/`);

export const approveQueueEntry = (entryId, { reviewer_notes } = {}) =>
  apiInstance.post(`${BASE}queue/${entryId}/approve/`, { reviewer_notes });

export const rejectQueueEntry = (entryId, { reviewer_notes } = {}) =>
  apiInstance.post(`${BASE}queue/${entryId}/reject/`, { reviewer_notes });

export const retryQueueEntry = (entryId) =>
  apiInstance.post(`${BASE}queue/${entryId}/retry/`);

export const updateQueueEntryMessages = (entryId, generated_messages) =>
  apiInstance.patch(`${BASE}queue/${entryId}/messages/`, { generated_messages });

// ---------- PM daily review queue ----------
// No campaign_id → returns sections[] grouped per campaign assigned to this PM.
// With campaign_id → returns a single campaign's review queue with meta.
export const getMyReviewQueue = (campaignId, { allPms } = {}) => {
  const p = new URLSearchParams();
  if (campaignId) p.set('campaign_id', String(campaignId));
  if (allPms)     p.set('all_pms', '1');   // admin: every PM's REVIEW entries for the campaign
  const qs = p.toString();
  return apiInstance.get(`${BASE}my/review-queue/${qs ? `?${qs}` : ''}`);
};
